import { Link } from "react-router-dom";
import { MapPin } from "lucide-react";
import { CardStatusTag, CardSaveButton } from "./CardHeaderActions";
import { Badge } from "@/components/ui/badge";
import { formatPriceExact, getPrimaryImg } from "@/utils";
import { FranchiseRequest } from "@/types";
import { useAuthStore } from "@/store/authStore";
import { PERMISSIONS } from "@/routes/guards";
import { useToggleSaveListing } from "@/hooks/useSavedListings";
import { cn } from "@/lib/utils";

type Props = {
  item: FranchiseRequest;
  isSelected?: boolean;
  onMouseEnter?: () => void;
};

export default function FranchiseRequestCard({ item, isSelected, onMouseEnter }: Props) {
  const { user } = useAuthStore();
  const { isSaved, toggleSave } = useToggleSaveListing();
  const saved = isSaved(item.id);
  const img = getPrimaryImg(item.images || []);

  // Chỉ hiển thị tag trạng thái cho người tạo hoặc Admin
  const showStatusTag = !!user && (user.id === item.creator?.id || user.role === 'admin');

  const categories = [item.desiredPropertyType, item.desiredBusinessType].filter(Boolean);
  const title = categories.length
    ? `Cần nhượng quyền ${categories.join(", ")}`
    : "Nhu cầu nhượng quyền mặt bằng";

  return (
    <Link
      to={`/nhu-cau-nhuong-quyen/${item.id}`}
      onMouseEnter={onMouseEnter}
      className={cn(
        "fnb-card block animate-fade-up h-full flex flex-col relative transition-all",
        isSelected && "ring-2 ring-blue-500 shadow-lg"
      )}
    >
      {/* Thumbnail */}
      <div className="h-44 overflow-hidden rounded-t-xl relative shrink-0">
        {img ? (
          <img
            src={img}
            alt={title}
            className="w-full h-full object-cover transition-transform duration-300 hover:scale-105"
          />
        ) : (
          <div
            className="w-full h-full flex items-center justify-center text-6xl"
            style={{ background: "var(--brand-pale)" }}
          >
            🔍
          </div>
        )}

        <CardStatusTag
          className="absolute top-2 left-2 z-10"
          show={showStatusTag}
          status={item.approvalStatus}
          label={item.approvalStatus === "approved" ? "Đã duyệt" : "Chờ duyệt"}
        />
      </div>

      {/* Body */}
      <div className="p-4 flex flex-col flex-1">
        <h3
          className="font-semibold text-sm leading-snug line-clamp-2 mb-3 pr-8"
          style={{ color: "var(--text-1)" }}
        >
          {title}
        </h3>

        {/* Budget */}
        <p
          className="font-display font-black text-lg mb-3"
          style={{ color: "var(--brand)" }}
        >
          {formatPriceExact(String(item.budgetMin))} - {formatPriceExact(String(item.budgetMax))} {item.currencyUnit}
        </p>

        {/* Requirements */}
        <p className="text-xs mb-3 line-clamp-2 text-gray-500">
          {item.additionalRequirements || "Không có yêu cầu thêm"}
        </p>

        {/* Type badges */}
        {categories.length > 0 && (
          <div className="flex gap-1.5 mb-3 flex-wrap">
            {categories.slice(0, 2).map((cat, idx) => (
              <Badge key={idx} variant="secondary" className="text-[10px]">
                {cat}
              </Badge>
            ))}
            {categories.length > 2 && (
              <Badge variant="secondary" className="text-[10px]">
                +{categories.length - 2}
              </Badge>
            )}
          </div>
        )}

        {/* Meta */}
        <div
          className="flex flex-wrap gap-1.5 pt-3 border-t mt-auto pr-10"
          style={{ borderColor: "var(--border-c)" }}
        >
          {item.district && item.province && (
            <span className="fnb-tag text-[11px]">
              <MapPin size={10} /> {item.district.name}, {item.province.name}
            </span>
          )}

          {item.requiredArea > 0 && (
            <span className="fnb-tag text-[11px]">
              {item.requiredArea} {item.areaUnit}
            </span>
          )}
        </div>
      </div>

      <CardSaveButton
        className="absolute bottom-4 right-4 z-10"
        saved={saved}
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          toggleSave(item.id, "FranchiseRequest");
        }}
      />
    </Link>
  );
}

